/** Keycloak Account / Admin credential row (no secrets). */
export type FlatCred = {
  id?: string
  type?: string
  userLabel?: string
  createdDate?: number
  secretData?: unknown
}

/** Shared default label for OTP / WebAuthn / recovery rows (hub + Vite DEV proxy). */
export function defaultMfaDeviceLabel(type: string, indexAmongType: number): string {
  const t = type.toLowerCase()
  if (t === 'otp' || t === 'totp' || t === 'hotp') {
    return indexAmongType === 0 ? 'Authenticator app' : `Authenticator app ${indexAmongType + 1}`
  }
  if (t.includes('webauthn')) {
    return indexAmongType === 0 ? 'Passkey / device' : `Passkey / device ${indexAmongType + 1}`
  }
  if (t.includes('recovery')) return 'Recovery codes'
  return type || 'Security device'
}

function isAccountCredentialContainer(x: unknown): boolean {
  if (!x || typeof x !== 'object') return false
  const o = x as Record<string, unknown>
  // CredentialContainer from Account API — never treat the type shell as a device.
  return (
    'userCredentials' in o ||
    'userCredentialMetadatas' in o ||
    'category' in o ||
    'createAction' in o ||
    'helpText' in o ||
    'displayName' in o
  )
}

function pushCredFromUnknown(raw: FlatCred[], boxType: string, entry: unknown): void {
  if (!entry || typeof entry !== 'object') return
  const o = entry as Record<string, unknown>
  // Real Account API shape: { credential: { id, type, userLabel, createdDate } }
  const nested =
    o.credential && typeof o.credential === 'object'
      ? (o.credential as Record<string, unknown>)
      : o
  const id = nested.id ?? nested.credentialId
  if (id == null || String(id).trim() === '') return
  const type = String(nested.type || boxType || 'unknown')
  if (type === 'password' || type === 'password-history') return
  raw.push({
    id: String(id),
    type,
    userLabel: nested.userLabel != null ? String(nested.userLabel) : undefined,
    createdDate:
      typeof nested.createdDate === 'number'
        ? nested.createdDate
        : typeof nested.createdDate === 'string' && nested.createdDate
          ? Number(nested.createdDate)
          : undefined,
  })
}

/**
 * Pull devices from Keycloak Account CredentialContainer JSON.
 * Account API uses `userCredentialMetadatas[].credential`, not flat Cred[].
 */
export function flattenAccountCredentials(parsed: unknown): FlatCred[] {
  if (!Array.isArray(parsed)) return []
  if (parsed.some(isAccountCredentialContainer)) {
    const raw: FlatCred[] = []
    for (const item of parsed) {
      if (!isAccountCredentialContainer(item)) continue
      const box = item as {
        type?: string
        userCredentials?: unknown[]
        userCredentialMetadatas?: unknown[]
      }
      const boxType = String(box.type || 'unknown')
      const list = box.userCredentialMetadatas ?? box.userCredentials ?? []
      for (const entry of list) pushCredFromUnknown(raw, boxType, entry)
    }
    return raw
  }
  return (parsed as FlatCred[]).filter((c) => Boolean(c?.id) && c.type !== 'password')
}
